"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Building2, Phone } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const publicSectorServices = [{
  title: "Totholzentfernung",
  description: "Fachgerechte Entnahme von abgestorbenen Ästen zur Vermeidung von Sach- und Personenschäden durch herabstürzendes Holz.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7019-scaled.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7019-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7019-scaled.png",
  compliance: "Sicherheit Prio 1",
  link: "/TreeCareStumpRemovalBuildingClearancePage"
}, {
  title: "Kronenpflege",
  description: "Erhaltungsschnitt zur Förderung der Vitalität und Stabilität Ihrer Bäume unter Berücksichtigung der natürlichen Wuchsform.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7070-scaled-e1778063324474.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7070-scaled.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/DSCF7070-scaled.png",
  compliance: "ZTV-Baumpflege",
  link: "/TreeCareStumpRemovalBuildingClearancePage"
}, {
  title: "Gebäudefreischnitt",
  description: "Präzises Einkürzen von Ästen, die Fassaden oder Dächer berühren.",
  image: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_35_06.png?v=b4glkg7f8n",
  srcset: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_35_06.png?v=encbzabi3wt-wvc-srcset",
  sizes: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_35_06.png?v=2tvhpr9ffhd-wvc-sizes",
  compliance: "Substanzschutz",
  link: "/TreeCareStumpRemovalBuildingClearancePage"
}, {
  title: "Kronensicherung",
  description: "Einbau von dynamischen oder statischen Sicherungssystemen zur Stabilisierung bruchgefährdeter Kronenteile.",
  image: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_47_02.png?v=y6gofhfrel",
  srcset: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_44_06.png?v=i5ditgfosmk-wvc-srcset",
  sizes: "https://balanced-monster.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-6.-Mai-2026-12_44_06.png?v=8hfup4fz2r6-wvc-sizes",
  compliance: "Baumerhalt",
  link: "/TreeCareStumpRemovalBuildingClearancePage"
}];
export default function PublicSectorExcellence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222570" id="servicesplitstack" className="w-full text-foreground py-32 px-6 !bg-[rgba(243,247,250,1)] md:px-8">
            <div ref={ref} className="max-w-7xl mx-auto transition-all duration-1000 ease-out opacity-100 translate-y-0">
                <Separator className="mb-16 bg-border/40" />

                <div className="flex flex-col items-center text-center mb-16">
                    
                    <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 tracking-tight">
                        Professionelle Baumpflege
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-3xl font-sans">
                        Wir sorgen für die Sicherheit und Vitalität Ihrer Bäume in Hamburg und Ahrensburg. 
                        Mit modernster Seilklettertechnik und fachlicher Expertise schützen wir Mensch und Natur.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
                    {publicSectorServices.map((service, i) => <div data-index={i} key={i} className="h-full">
                            <Card className="group h-full flex flex-col overflow-hidden border-border/50 shadow-sm hover:shadow-xl transition-all duration-500 bg-card hover:-translate-y-1">
                                <div className="relative w-full h-48 overflow-hidden bg-muted">
                                    <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-in-out transform group-hover:scale-110 group-hover:-rotate-3" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                </div>
                                
                                <CardHeader className="pb-4">
                                    <div className="mb-3">
                                        <Badge variant="outline" className="bg-accent/50 backdrop-blur-sm border-primary/30 text-primary font-mono text-xs tracking-wider">
                                            <ShieldCheck className="size-3.5 mr-1.5" />
                                            {service.compliance}
                                        </Badge>
                                    </div>
                                    <CardTitle className="font-default text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                                        {service.title}
                                    </CardTitle>
                                </CardHeader>
                                
                                <CardContent className="flex-grow">
                                    <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                                        {service.description}
                                    </CardDescription>
                                </CardContent>
                                
                                <CardFooter className="pt-0 pb-6" />
                            </Card>
                        </div>)}
                </div>

                <div className="flex justify-center mt-12">
                    <Button asChild size="lg" className="bg-destructive hover:bg-destructive/90 text-white rounded-lg px-8 py-6 text-lg font-semibold shadow-lg transition-all duration-300 hover:scale-105">
                        <Link to="#footer">
                            <Phone className="mr-2 size-5" />
                            Jetzt anrufen
                        </Link>
                    </Button>
                </div>
            </div>
        </section>;
}